import React, { useCallback, useContext } from 'react';

import { useQuery } from '@tanstack/react-query';

import { useNavigate } from 'react-router-dom';
import { AuthContext } from 'contexts/AuthContext';
import { useViewAs } from 'hooks/useViewAs';
import { ROLE_ADMIN } from 'variables/constants';

import {
  Text,
  Table,
  Box,
  Flex,
  useColorModeValue,
  Thead,
  Tr,
  Td,
  Th,
  Tbody,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
} from '@chakra-ui/react';

import Card from 'components/card/Card';
import Paginate from 'components/paginate/Paginate';
import { SortableTh } from 'components/table';
import type { SortDirection } from 'components/table';

import { apiAllLeadList } from 'api';

import getDateTimeString from 'helpers/getDateTimeString';

import LeadSearch from 'views/leads/components/LeadSearch';

import type {
  APIPaginatedResponse,
  APILeadsListResultData,
} from 'types/api.types';

import { useUrlFilters } from 'hooks/useUrlFilters';

import { LEAD_ALL_STATUSES } from 'views/leads/constants';

const TABLE_HEADERS: { label: string; field?: string }[] = [
  { label: 'Lead Name', field: 'client_name' },
  { label: 'Workflow', field: 'workflow' },
  { label: 'Source', field: 'source' },
  { label: 'Assigned Broker', field: 'assigned_broker' },
  { label: 'Status' },
  { label: 'Last Modified', field: 'last_modified' },
  { label: 'Created', field: 'created_at' },
];

const ALL_LEADS_FILTER_KEYS = [
  'search',
  'source',
  'workflow',
  'status',
] as const;

function parseQueryToFilters(query: string): Record<string, string> {
  const out: Record<string, string> = {};
  if (!query) return out;
  query.split('&').forEach((part) => {
    const eq = part.indexOf('=');
    if (eq > 0) {
      const k = decodeURIComponent(part.slice(0, eq)).trim();
      const v = decodeURIComponent(part.slice(eq + 1)).trim();
      if (k && v) out[k] = v;
    }
  });
  return out;
}

const AllLeadsList = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === ROLE_ADMIN;
  const {
    getFilter,
    setFilters,
    setFiltersAndPage,
    page,
    setPage,
    queryString,
    searchParams,
  } = useUrlFilters(ALL_LEADS_FILTER_KEYS);

  const sortField = searchParams.get('sort_field') ?? '';
  const sortDirection = (
    searchParams.get('sort_direction') === 'desc' ? 'desc' : 'asc'
  ) as SortDirection;
  const sortQuery = sortField
    ? `sort_field=${sortField}&sort_direction=${sortDirection}`
    : '';
  const listQuery = [queryString, sortQuery].filter(Boolean).join('&');

  const handleSort = useCallback(
    (field: string) => {
      const nextDirection =
        searchParams.get('sort_field') === field &&
        searchParams.get('sort_direction') !== 'desc'
          ? 'desc'
          : 'asc';
      setFiltersAndPage(
        { sort_field: field, sort_direction: nextDirection },
        0,
      );
    },
    [searchParams, setFiltersAndPage],
  );

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const viewAs = useViewAs();

  const { error, data, isLoading, isFetching } = useQuery({
    queryKey: ['all-lead-list', page, listQuery, viewAs.filterParams],
    queryFn: () =>
      apiAllLeadList(
        page + 1,
        listQuery,
        viewAs.viewAsEmail,
        viewAs.viewAsBranchId,
      ),
    refetchInterval: 120000,
    // Admin auto-loads (bucket counts all org active leads); view-as auto-loads; others need search
    enabled: !!queryString || viewAs.isViewingAs || isAdmin,
    refetchOnWindowFocus: false,
  }) as {
    data: APIPaginatedResponse<APILeadsListResultData>;
    isLoading: boolean;
    error: any;
    isFetching: boolean;
  };

  const onLeadClick = (lead: APILeadsListResultData) => {
    navigate(`/admin/leads/${lead.id}`);
  };

  const handleSearch = useCallback(
    (query: string) => {
      setFilters(parseQueryToFilters(query));
    },
    [setFilters],
  );

  return (
    <Card
      flexDirection="column"
      w="100%"
      px="0px"
      overflowX={{ sm: 'scroll', lg: 'hidden' }}
      mb="20px"
    >
      <Flex px="20px" align="center" justify="space-between">
        <Text
          color={textColor}
          fontSize="22px"
          fontWeight="700"
          lineHeight="100%"
        >
          All Leads
        </Text>
      </Flex>
      <Box mt="20px">
        <LeadSearch
          onSearch={handleSearch}
          leadStatues={LEAD_ALL_STATUSES}
          initialSearch={getFilter('search')}
          initialSource={getFilter('source')}
          initialWorkflow={getFilter('workflow')}
          initialStatus={getFilter('status')}
        />
      </Box>

      {isLoading && (queryString || viewAs.isViewingAs || isAdmin) && (
        <Box textAlign="center" mt="20px">
          <Spinner size="md" />
          <Text mt={2}>Loading leads...</Text>
        </Box>
      )}

      {error && (queryString || viewAs.isViewingAs || isAdmin) && (
        <Box p="20px" pb="0">
          <Alert status="error">
            <AlertIcon />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{JSON.stringify(error)}</AlertDescription>
          </Alert>
        </Box>
      )}

      {!queryString && !viewAs.isViewingAs && !isAdmin && (
        <Box textAlign="center" mt="20px" color="gray.500">
          Enter search terms and click Search to find leads
        </Box>
      )}

      {(queryString || viewAs.isViewingAs || isAdmin) && data && (
        <>
          <Table variant="simple" color="gray.500" mt="20px">
            <Thead>
              <Tr>
                {TABLE_HEADERS.map(({ label, field }) => {
                  const thProps = {
                    borderColor,
                    borderTop: '1px solid',
                    borderTopColor: borderColor,
                  };

                  return field ? (
                    <SortableTh
                      key={label}
                      label={label}
                      field={field}
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                      {...thProps}
                    />
                  ) : (
                    <Th key={label} pe="10px" color="gray.400" {...thProps}>
                      {label}
                    </Th>
                  );
                })}
              </Tr>
            </Thead>

            <Tbody>
              {isFetching && (
                <Tr>
                  <Td colSpan={TABLE_HEADERS.length} textAlign="center">
                    <Flex align="center" justify="center">
                      <Spinner size="sm" mr="10px" /> Updating...
                    </Flex>
                  </Td>
                </Tr>
              )}

              {data?.results.map((lead) => (
                <Tr
                  key={lead.id}
                  onClick={() => onLeadClick(lead)}
                  cursor="pointer"
                  _hover={{
                    bg: 'gray.200',
                  }}
                >
                  <Td>
                    <Flex align="center">
                      <Text color={textColor} fontSize="sm" fontWeight="700">
                        {lead.first_name} {lead.last_name}
                      </Text>
                    </Flex>
                  </Td>
                  <Td>
                    <Text color={textColor} fontSize="sm" fontWeight="700">
                      {lead.workflow?.name || 'None'}
                    </Text>
                  </Td>
                  <Td>
                    <Text color={textColor} fontSize="sm" fontWeight="700">
                      {lead.source?.name || 'None'}
                    </Text>
                  </Td>
                  <Td>
                    <Text color={textColor} fontSize="sm" fontWeight="700">
                      {lead.user
                        ? `${lead.user.first_name} ${lead.user.last_name}`
                        : 'Unassigned'}
                    </Text>
                  </Td>
                  <Td>
                    <Text color={textColor} fontSize="sm" fontWeight="700">
                      {lead.status_display}
                    </Text>
                  </Td>
                  <Td>
                    <Text color={textColor} fontSize="sm" fontWeight="700">
                      {getDateTimeString(new Date(lead.updated_at))}
                    </Text>
                  </Td>
                  <Td>
                    <Text color={textColor} fontSize="sm" fontWeight="700">
                      {getDateTimeString(new Date(lead.created_at))}
                    </Text>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>

          {data?.results.length === 0 && (
            <Box textAlign="center" w="100%" mt="20px" mb="20px">
              No leads found
            </Box>
          )}

          <Paginate page={page} count={data?.count || 0} onSetPage={setPage} />
        </>
      )}
    </Card>
  );
};

export default React.memo(AllLeadsList);
